import { uid } from "./entities";

// Catálogo de proveedores soportados por la estructura de Conexiones.
// Fase 1: solo Apimart está disponible para crear conexiones. La estructura
// (nombre, proveedor, capacidades, estado) permite añadir otros proveedores
// en el futuro mediante integraciones independientes, sin rediseñar el modelo.
export const CONNECTION_PROVIDERS = [
  { id: "apimart", label: "Apimart", capabilities: ["image", "video"], available: true },
  { id: "kie", label: "Kie.ai", capabilities: ["image", "video"], available: true },
  { id: "muapi", label: "MuAPI", capabilities: ["image", "video"], available: true },
  { id: "fal", label: "fal.ai", capabilities: ["upscale"], available: true },
  { id: "elevenlabs", label: "ElevenLabs", capabilities: ["audio"], available: false },
];

export const providerById = (id) => CONNECTION_PROVIDERS.find((p) => p.id === id) || null;

// Conexión operativa: proveedor real (no demo) con integración disponible.
// Una conexión es operativa si es de Apimart, Kie.ai o MuAPI y no es una conexión de demo.
export const isConnectionOperational = (connection) =>
  !connection.isDemo && (connection.provider === "apimart" || connection.provider === "kie" || connection.provider === "muapi");

export const Connection = (c = {}) => ({
  id: uid("con"),
  name: "",
  provider: "apimart",
  capabilities: ["image", "video"],
  status: "not_configured", // Fase 1: siempre "not_configured"; nunca se persiste una clave real.
  isDemo: false,
  createdAt: new Date().toISOString(),
  ...c,
});

/**
 * Sincroniza las conexiones locales con el catálogo del backend.
 * Si APIMart, Kie.ai o MuAPI están configurados en el backend (key_configured: true),
 * se representan como conexión operativa sin inventar estados ni crear duplicados.
 */
export const syncConnectionsWithCatalog = (connections = [], catalog = null) => {
  if (!catalog?.providers) return connections;
  let nextConnections = [...connections];

  // Si el catálogo restringe los proveedores permitidos (ej. invitados a solo apimart), filtrar
  if (Array.isArray(catalog.allowed_providers)) {
    nextConnections = nextConnections.filter((c) => !c.provider || catalog.allowed_providers.includes(c.provider));
  }

  for (const providerId of ["apimart", "kie", "muapi"]) {
    if (Array.isArray(catalog.allowed_providers) && !catalog.allowed_providers.includes(providerId)) {
      continue;
    }
    const prov = catalog.providers.find((p) => p.provider_id === providerId);
    const isConfigured = Boolean(prov?.key_configured);

    if (isConfigured) {
      const existing = nextConnections.find((c) => !c.isDemo && c.provider === providerId);
      if (existing) {
        nextConnections = nextConnections.map((c) =>
          c.id === existing.id ? { ...c, status: "configured" } : c
        );
      } else {
        const demoProv = nextConnections.find((c) => c.provider === providerId && c.isDemo);
        if (demoProv) {
          nextConnections = nextConnections.map((c) =>
            c.id === demoProv.id
              ? { ...c, name: c.name.replace(/\s*—\s*demo$/i, "").trim() || (prov.label || providerId), isDemo: false, status: "configured" }
              : c
          );
        } else {
          nextConnections.push(
            Connection({
              id: `con_${providerId}`,
              name: prov.label || (providerId === "kie" ? "Kie.ai" : providerId === "muapi" ? "MuAPI" : "Apimart"),
              provider: providerId,
              capabilities: prov.capabilities || ["image", "video"],
              status: "configured",
              isDemo: false,
            })
          );
        }
      }
    } else {
      nextConnections = nextConnections.map((c) =>
        (!c.isDemo && c.provider === providerId && c.status === "configured")
          ? { ...c, status: "not_configured" }
          : c
      );
    }
  }

  return nextConnections;
};

// Modelos y capacidades DEMO. Permiten probar la selección de modelo, formato,
// resolución, cantidad y coste estimado sin inventar compatibilidades ni tarifas
// reales de Apimart. Se sustituirán por la matriz verificada en Fase 3.
export const DEMO_IMAGE_MODELS = [
  {
    id: "demo-image-standard",
    provider: "apimart",
    label: "Demo · Imagen estándar",
    isDemo: true,
    formats: ["auto", "1:1", "4:3", "3:4", "3:2", "2:3", "4:5", "16:9", "9:16"],
    extendedFormats: ["21:9", "4:1", "1:4", "8:1", "1:8"],
    resolutions: ["1K", "2K"],
    maxQuantity: 4,
    costPerImage: { "1K": 0.02, "2K": 0.05 },
  },
  {
    id: "demo-image-pro",
    provider: "apimart",
    label: "Demo · Imagen Pro",
    isDemo: true,
    formats: ["auto", "1:1", "4:3", "3:4", "3:2", "2:3", "4:5", "16:9", "9:16"],
    extendedFormats: ["21:9", "4:1", "1:4", "8:1", "1:8"],
    resolutions: ["1K", "2K", "4K"],
    maxQuantity: 4,
    costPerImage: { "1K": 0.04, "2K": 0.08, "4K": 0.15 },
  },
];

export const DEMO_VIDEO_MODELS = [
  {
    id: "demo-video-t2v",
    provider: "apimart",
    label: "Demo · Texto a vídeo",
    isDemo: true,
    modes: ["text_to_video"],
    formats: ["16:9", "9:16", "1:1"],
    resolutions: ["480p", "540p", "720p"],
    durations: [4, 8],
    costPerSecond: { "480p": 0.01, "540p": 0.015, "720p": 0.025 },
  },
  {
    id: "demo-video-i2v",
    provider: "apimart",
    label: "Demo · Imagen / fotogramas a vídeo",
    isDemo: true,
    modes: ["image_to_video", "first_last_frame"],
    formats: ["16:9", "9:16"],
    resolutions: ["540p", "720p", "1080p"],
    durations: [4, 8, 12],
    costPerSecond: { "540p": 0.02, "720p": 0.035, "1080p": 0.06 },
  },
];

export const modelsFor = (kind) => (kind === "video" ? DEMO_VIDEO_MODELS : DEMO_IMAGE_MODELS);
export const modelById = (kind, id) => modelsFor(kind).find((m) => m.id === id) || null;

// Conexiones compatibles con una capacidad dada (image | video | text).
export const connectionsForCapability = (connections, capability) =>
  connections.filter((c) => c.capabilities.includes(capability));

export const cheapestVideoCombo = (model) => {
  if (!model) return null;
  const resolution = model.resolutions[0];
  const duration = Math.min(...model.durations);
  const cost = model.costPerSecond[resolution] * duration;
  return { resolution, duration, quantity: 1, cost };
};
